import { ThemeColors } from '@/src/context/ThemeContext';
import { PublicPost } from '@/src/domain/models';
import { useTranslation } from '@/src/i18n';
import { Ionicons } from '@expo/vector-icons';
import { useRouter } from 'expo-router';
import { Image, Linking, Platform, SafeAreaView, ScrollView, Text, TouchableOpacity, View } from 'react-native';

const AppIcon = require('@/assets/images/android-icon-foreground.png');
import { styles } from './PostDetailScreen.styles';

interface PostDetailScreenProps {
  post: PublicPost;
  postUrl: string;
  colors: ThemeColors;
  isAuthenticated?: boolean;
  onTournamentPress?: (tournamentId: string) => void;
}

const formatTimestamp = (date: Date) => {
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
};

export const PostDetailScreen = ({
  post,
  postUrl,
  colors,
  isAuthenticated = false,
  onTournamentPress,
}: PostDetailScreenProps) => {
  const { t } = useTranslation();
  const router = useRouter();
  const displayName = post.userName || t('misc.unknownUser');
  const isWeb = Platform.OS === 'web';

  const handleOpenInApp = () => {
    Linking.openURL(`tournaly://post/${post.id}`).catch(() => {});
  };

  const handleDownloadApp = () => {
    const iosUrl = 'https://apps.apple.com/app/tournaly/id0000000000';
    const androidUrl = 'https://play.google.com/store/apps/details?id=com.tournaly.app';
    const url = /iPhone|iPad|iPod/i.test(navigator?.userAgent ?? '') ? iosUrl : androidUrl;
    if (typeof window !== 'undefined') {
      window.open(url, '_blank');
    }
  };

  const handleSignUp = () => {
    router.push('/auth/register' as any);
  };

  const postCard = (
    <View style={[styles.card, { backgroundColor: colors.card, borderColor: colors.border, shadowColor: colors.shadow }]}>
      {/* Header */}
      <View style={styles.header}>
        {post.userAvatarUrl ? (
          <Image source={{ uri: post.userAvatarUrl }} style={styles.avatar} />
        ) : (
          <View style={[styles.avatarPlaceholder, { backgroundColor: colors.border }]}>
            <Ionicons name="person" size={22} color={colors.textSecondary} />
          </View>
        )}
        <View>
          <Text style={[styles.userName, { color: colors.text }]}>{displayName}</Text>
          <Text style={[styles.timestamp, { color: colors.textTertiary }]}>
            {formatTimestamp(post.createdAt)}
          </Text>
        </View>
      </View>

      {/* Content */}
      <Text style={[styles.content, { color: colors.text }]}>{post.content}</Text>

      {/* Image */}
      {post.imageUrl && (
        <Image source={{ uri: post.imageUrl }} style={styles.image} resizeMode="cover" />
      )}

      {/* Like count + tournament badge row */}
      {(post.likeCount > 0 || (post.tournamentId && post.tournamentName)) && (
        <View style={[styles.metaRow, { borderTopColor: colors.divider }]}>
          {post.tournamentId && post.tournamentName && (
            <TouchableOpacity
              style={[styles.tournamentBadge, { backgroundColor: colors.border }]}
              onPress={() => onTournamentPress?.(post.tournamentId!)}
              disabled={!onTournamentPress}
            >
              <Ionicons name="trophy-outline" size={14} color={colors.primary} />
              <Text style={[styles.tournamentName, { color: colors.text }]} numberOfLines={1}>
                {post.tournamentName}
              </Text>
            </TouchableOpacity>
          )}
          <View style={styles.metaSpacer} />
          {post.likeCount > 0 && (
            <View style={styles.likeRow}>
              <Ionicons name="heart" size={16} color={colors.error} />
              <Text style={[styles.likeCount, { color: colors.textSecondary }]}>
                {post.likeCount}
              </Text>
            </View>
          )}
        </View>
      )}
    </View>
  );

  // Authenticated: simple view, no CTA
  if (isAuthenticated) {
    return (
      <ScrollView
        style={[styles.container, { backgroundColor: colors.background }]}
        contentContainerStyle={styles.scrollContent}
      >
        {postCard}
      </ScrollView>
    );
  }

  // Unauthenticated: standalone branded page
  return (
    <SafeAreaView style={[styles.standaloneContainer, { backgroundColor: colors.background }]}>
      {/* Top bar */}
      <View style={[styles.topBar, { backgroundColor: colors.surface, borderBottomColor: colors.border }]}>
        <View style={styles.topBarLogo}>
          <Image source={AppIcon} style={styles.topBarIcon} />
          <Text style={[styles.topBarBrand, { color: colors.text }]}>Tournaly</Text>
        </View>
        <TouchableOpacity
          style={[styles.topBarSignUp, { borderColor: colors.primary }]}
          onPress={handleSignUp}
        >
          <Text style={[styles.topBarSignUpText, { color: colors.primary }]}>
            {t('auth.signUp')}
          </Text>
        </TouchableOpacity>
      </View>

      <ScrollView
        style={styles.container}
        contentContainerStyle={styles.standaloneScrollContent}
        showsVerticalScrollIndicator={false}
      >
        {postCard}

        {/* CTA section */}
        <View style={[styles.ctaSection, { backgroundColor: colors.surface, borderColor: colors.border }]}>
          <Image source={AppIcon} style={styles.ctaIcon} />
          <Text style={[styles.ctaTitle, { color: colors.text }]}>
            {t('post.ctaTitle')}
          </Text>
          <Text style={[styles.ctaSubtitle, { color: colors.textSecondary }]}>
            {t('post.ctaSubtitle')}
          </Text>

          <TouchableOpacity
            style={[styles.ctaPrimaryButton, { backgroundColor: colors.primary }]}
            onPress={handleSignUp}
          >
            <Ionicons name="person-add-outline" size={18} color="#fff" />
            <Text style={styles.ctaPrimaryButtonText}>{t('post.ctaSignUp')}</Text>
          </TouchableOpacity>

          {isWeb && (
            <TouchableOpacity style={styles.ctaSecondaryButton} onPress={handleOpenInApp}>
              <Ionicons name="phone-portrait-outline" size={16} color={colors.textSecondary} />
              <Text style={[styles.ctaSecondaryButtonText, { color: colors.textSecondary }]}>
                {t('post.openInApp')}
              </Text>
            </TouchableOpacity>
          )}
        </View>
      </ScrollView>
    </SafeAreaView>
  );
};
